/**
 * IndicatorAnchor — composable wrapper that places an `<Indicator>` at
 * a corner of an arbitrary host element WITHOUT being clipped by the
 * host's own `overflow: hidden`.
 *
 * The classic problem: notification badges are typically positioned in
 * the top-right corner of a button or avatar, half-overlapping the host
 * so the badge "sticks out". In our system multiple hosts clip their
 * children (`SegmentedButton` clips cell content to keep rounded outer
 * corners; `Button` has its own rounded border). Placing an absolute
 * indicator INSIDE the clipping host would cut the indicator at the
 * border.
 *
 * Solution: the indicator goes on an OUTER wrapper that is positioned
 * but NOT clipped. The host renders as a regular child of that wrapper.
 * The indicator is `position: absolute` at the corner — free to extend
 * past the host's rounded boundary because the wrapper allows overflow.
 *
 *   <IndicatorAnchor indicator={<Indicator count={5} />}>
 *     <Button>Inbox</Button>
 *   </IndicatorAnchor>
 *
 * Notes on hosts that already clip themselves:
 *   - `SegmentedButton` previously clipped the entire row, which made
 *     in-cell indicators impossible. After the matching refactor, the
 *     Row drops `overflow: hidden` and individual cells round their own
 *     outer corners — letting an IndicatorAnchor on a cell stick out
 *     normally.
 *   - `Button` rounds with `border-radius` but does not need
 *     `overflow: hidden` itself (it has no internal children that paint
 *     past the radius), so anchoring on top of it works directly.
 *
 * @format
 */

import {forwardRef, type CSSProperties, type ReactNode} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

export const INDICATOR_ANCHOR_POSITIONS = [
  'top-right',
  'top-left',
  'bottom-right',
  'bottom-left',
] as const;
export type IndicatorAnchorPosition = (typeof INDICATOR_ANCHOR_POSITIONS)[number];

export interface IndicatorAnchorProps {
  /**
   * The Indicator (or any ReactNode) to pin to the corner. Pass
   * `<Indicator …>` from `../../atoms/Indicator`. The wrapper does not
   * style the node — it only positions it.
   *
   * Omit / pass `null` to render the host without an indicator. Useful
   * for the "count=0 → no badge" pattern: compute the indicator
   * conditionally at the call site.
   */
  indicator: ReactNode;

  /** Corner the indicator sits on. Default `'top-right'`. */
  position?: IndicatorAnchorPosition;

  /**
   * How far the indicator overlaps with the host edges, in pixels.
   * Positive values pull the indicator INWARD (the badge sits more on
   * top of the host); negative values push it OUTWARD (the badge
   * floats away from the corner). Default `4` — half the dot diameter
   * for the most common `size="m"` count.
   */
  overlap?: number;

  /** Host element to anchor against. */
  children: ReactNode;

  className?: string;
  style?: CSSProperties;
}

// ── Styled wrapper ─────────────────────────────────────────────────────

/**
 * The wrapper takes its size from the host — `display: inline-block`
 * shrink-wraps around the host's intrinsic size. `position: relative`
 * provides the anchor for the absolute indicator. CRUCIALLY no
 * `overflow: hidden` here — that's the whole point.
 *
 * `vertical-align: middle` keeps the wrapper aligned with text baselines
 * when it appears inside a paragraph or inline run.
 */
const Wrapper = styled(Box)({
  display: 'inline-block',
  position: 'relative',
  verticalAlign: 'middle',
  lineHeight: 0,
});

const IndicatorSlot = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{
  $position: IndicatorAnchorPosition;
  $overlap: number;
}>(({$position, $overlap}) => {
  // Translate-50% on each axis would center the indicator on the corner
  // point. Combined with the overlap inset, the indicator sits half-on /
  // half-off depending on overlap.
  const offset = -$overlap;
  const vertical = $position.startsWith('top') ? {top: offset} : {bottom: offset};
  const horizontal = $position.endsWith('right') ? {right: offset} : {left: offset};
  const tx = $position.endsWith('right') ? '50%' : '-50%';
  const ty = $position.startsWith('top') ? '-50%' : '50%';
  return {
    position: 'absolute',
    ...vertical,
    ...horizontal,
    transform: `translate(${tx}, ${ty})`,
    zIndex: 1,
    pointerEvents: 'none',
    lineHeight: 0,
  };
});

// ── Component ──────────────────────────────────────────────────────────

export const IndicatorAnchor = forwardRef<HTMLDivElement, IndicatorAnchorProps>(
  function IndicatorAnchor(
    {indicator, position = 'top-right', overlap = 4, children, className, style},
    ref,
  ) {
    return (
      <Wrapper ref={ref} className={className} style={style}>
        {children}
        {indicator != null && (
          <IndicatorSlot $position={position} $overlap={overlap}>
            {indicator}
          </IndicatorSlot>
        )}
      </Wrapper>
    );
  },
);
